import * as React from 'react';
import {FunctionComponent} from 'react';
import {useElementSize} from 'usehooks-ts';
import {CalendarEvent} from '../../../../../Models/CalendarEvent';
import {PartialWorkout} from '../../../../../Models/PartialWorkout';
import {Distance} from '../../../../../Domain/Distance';
import {format} from 'date-fns';
import {Link} from 'react-router-dom';
import {formatDistanceCompact} from '../../../../Support/Formatting/formatDistance';
import {classNames} from '../../../../Support/classNames';

interface OwnProps {
    isEmpty?: boolean;
    isActive?: boolean;
    number?: number;
    date: Date;
    ownWorkouts: PartialWorkout[];
    calendarEvents: CalendarEvent[];
    onStartDragging: (calendarEvent: CalendarEvent) => void;
    onDrop: (date: Date) => void;
    onCreateClick?: () => void;
    onCalendarEventClick: (calendarEvent: CalendarEvent) => void;
}

export const Day: FunctionComponent<OwnProps> = ({
    isEmpty,
    isActive,
    number,
    date,
    ownWorkouts,
    calendarEvents,
    onStartDragging,
    onDrop,
    onCalendarEventClick,
    onCreateClick,
}) => {
    const [numberRef, numberDimensions] = useElementSize();
    const [contentRef, contentDimensions] = useElementSize();
    const cellPaddingPx = 8;
    const eventHeightPx = 21;

    let maxEventsInView = Math.floor(
        (contentDimensions.height - numberDimensions.height - cellPaddingPx * 2) / eventHeightPx,
    );

    //Randomly add an activity
    const hasActivity = ownWorkouts.length > 0;

    const cumulativeDistanceOwnWorkouts = ownWorkouts.reduce((acc, workout) => {
        return acc.add(
            workout.workout_part_results.reduce((workoutAcc, workoutPartResult) => {
                return workoutAcc.add(workoutPartResult.traveled_distance);
            }, Distance.zero()),
        );
    }, Distance.zero());

    const cumulativeDistanceOwnWorkoutsFormatted = formatDistanceCompact(cumulativeDistanceOwnWorkouts);

    if (hasActivity) {
        maxEventsInView--;
    }

    let visibleEventCount = calendarEvents.length;
    let overflowText = null;
    if (maxEventsInView < calendarEvents.length) {
        //-1 to make room for the overflow
        visibleEventCount = maxEventsInView - 1;

        overflowText = `+${calendarEvents.length - visibleEventCount} more`;
    }

    if (isEmpty) {
        return <div className="day day--empty"></div>;
    } else {
        return (
            <div
                className={classNames('day', {
                    'day--active': isActive,
                })}
            >
                <div className="day__content">
                    {/*<div className="day__cumulative-activity">15k</div>*/}
                    {onCreateClick && (
                        <button className="day__btn btn btn-outline-secondary" onClick={() => onCreateClick()}>
                            Create
                        </button>
                    )}
                    <span className="day__number" ref={numberRef}>
                        <span>{number}</span>
                    </span>
                    <div
                        className="day__events"
                        ref={contentRef}
                        onDragOver={(e) => {
                            e.preventDefault();
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            onDrop(date);
                        }}
                    >
                        {calendarEvents.slice(0, visibleEventCount).map((calendarEvent, index) => (
                            <button
                                draggable
                                onDragStart={() => {
                                    onStartDragging(calendarEvent);
                                }}
                                className="day__event"
                                key={index}
                                onClick={() => onCalendarEventClick(calendarEvent)}
                            >
                                {calendarEvent.title}
                            </button>
                        ))}
                        {overflowText && <div className="day__event day__event--overflow">{overflowText}</div>}
                        {hasActivity && ownWorkouts.length > 1 && (
                            <Link
                                className="day__own-activity"
                                to={
                                    '/workouts?dateRange.from=date.' +
                                    format(date, 'yyyy-MM-dd') +
                                    '&dateRange.till=date.' +
                                    format(date, 'yyyy-MM-dd')
                                }
                            >
                                {cumulativeDistanceOwnWorkoutsFormatted.formatted}
                                {cumulativeDistanceOwnWorkoutsFormatted.unit}
                            </Link>
                        )}
                        {hasActivity && ownWorkouts.length === 1 && (
                            <Link className="day__own-activity" to={'/workouts/show/' + ownWorkouts[0].id}>
                                {cumulativeDistanceOwnWorkoutsFormatted.formatted}
                                {cumulativeDistanceOwnWorkoutsFormatted.unit}
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        );
    }
};
